<template>
  <v-app>
    <!-- App Bar -->
    <v-app-bar dark flat style="background-color: #800000;">
      <v-toolbar-title>Admin Home</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="logOut">
        <v-icon>mdi-logout</v-icon>
      </v-btn>
    </v-app-bar>

    <!-- Main Content -->
    <v-main>
      <v-container fluid class="py-6" style="background: #f9f9f9;">
        <v-row justify="center">
          <v-col cols="12" md="10" lg="8">
            <!-- Calendar Card -->
            <v-card class="pa-4 elevation-10">
              <h2 class="text-h6 font-weight-bold mb-4 text-center">Church Events Calendar</h2>

              <!-- Calendar Header -->
              <v-row align="center" justify="space-between" class="mb-2">
                <v-btn icon @click="prevMonth">
                  <v-icon>mdi-chevron-left</v-icon>
                </v-btn>

                <div class="text-h6 font-weight-medium text-center">
                  {{ formattedMonth }}
                </div>

                <v-btn icon @click="nextMonth">
                  <v-icon>mdi-chevron-right</v-icon>
                </v-btn>
              </v-row>

              <!-- Calendar Display -->
              <v-sheet :height="calendarHeight" class="my-4">
                <v-calendar
                  v-model="selectedDate"
                  :type="type"
                  :value="calendarView"
                  :now="today"
                  color="#800000"
                  :events="events"
                  event-color="#800000"
                  @click:date="openDialog"
                  @click:event="editEvent"
                  class="full-width"
                />
              </v-sheet>

              <!-- Event Dialog -->
              <v-dialog v-model="dialog" max-width="500">
                <v-card>
                  <v-card-title class="headline">{{ isEditing ? 'Edit Event' : 'Add Event' }}</v-card-title>
                  <v-card-text>
                    <v-text-field v-model="eventForm.title" label="Event Title" dense outlined />
                    <v-textarea v-model="eventForm.description" label="Description" rows="2" dense outlined />
                    <v-text-field v-model="eventForm.date" label="Date" dense outlined readonly />
                    <v-text-field v-model="eventForm.startTime" label="Start Time (e.g., 10:00 AM)" dense outlined />
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="red" text v-if="isEditing" @click="deleteEvent">Delete</v-btn>
                    <v-btn text @click="dialog = false">Cancel</v-btn>
                    <v-btn color="primary" text @click="saveEvent">{{ isEditing ? 'Update' : 'Save' }}</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </v-card>

            <!-- Access Requests Section -->
            <v-card class="mt-6 pa-4 elevation-8">
              <div class="d-flex justify-space-between align-center mb-4">
                <h3 class="text-h6 font-weight-bold">Recent Access Requests</h3>
                <v-btn small color="primary" @click="$router.push('/admin-access-requests')">
                  More
                  <v-icon right>mdi-chevron-right</v-icon>
                </v-btn>
              </div>

              <v-list dense>
                <v-list-item v-for="(request, index) in accessRequests.slice(0, 3)" :key="index">
                  <v-list-item-content>
                    <v-list-item-title class="font-weight-bold">
                      {{ request.firstName }} {{ request.lastName }}
                    </v-list-item-title>
                    <v-list-item-subtitle>{{ request.email }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item v-if="accessRequests.length === 0">
                  <v-list-item-content>
                    <v-list-item-title>No access requests yet</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-main>

    <!-- Bottom Navigation -->
    <v-bottom-navigation app grow>
      <v-btn icon to="/admin-home">
        <v-icon>mdi-home</v-icon>
        <span class="tab-label">Home</span>
      </v-btn>
      <v-btn icon to="/admin-search">
        <v-icon>mdi-magnify</v-icon>
        <span class="tab-label">Search</span>
      </v-btn>
      <v-btn icon to="/admin-messages">
        <v-icon>mdi-message-text-outline</v-icon>
        <span class="tab-label">Messages</span>
      </v-btn>
      <v-btn icon to="/minister-prayer-requests">
        <v-icon>mdi-hands-pray</v-icon>
        <span class="tab-label">Prayer</span>
      </v-btn>
      <v-btn icon to="/admin-settings">
        <v-icon>mdi-cog</v-icon>
        <span class="tab-label">Settings</span>
      </v-btn>
    </v-bottom-navigation>
  </v-app>
</template>

<script>
import { signOut } from "firebase/auth";
import { auth, db } from "@/firebase";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";

export default {
  data() {
    return {
      // Calendar
      selectedDate: new Date().toISOString().substr(0, 10),
      calendarView: new Date().toISOString().substr(0, 10),
      today: new Date().toISOString().substr(0, 10),
      type: "month",
      events: [],
      dialog: false,
      isEditing: false,
      eventForm: {
        id: null,
        title: "",
        description: "",
        date: "",
        startTime: "",
      },

      // Access Requests
      accessRequests: [],
    };
  },

  computed: {
    calendarHeight() {
      return this.$vuetify.breakpoint.smAndDown ? 450 : 600;
    },
    formattedMonth() {
      const date = new Date(this.calendarView);
      return date.toLocaleString("default", {
        month: "long",
        year: "numeric",
      });
    },
  },

  methods: {
    // Authentication
    async logOut() {
      try {
        await signOut(auth);
        this.$router.push("/");
      } catch (error) {
        console.error("Log out error:", error);
      }
    },

    // Calendar Navigation
    nextMonth() {
      const date = new Date(this.calendarView);
      date.setMonth(date.getMonth() + 1);
      const newDate = date.toISOString().substr(0, 10);
      this.calendarView = newDate;
      this.selectedDate = newDate;
    },

    prevMonth() {
      const date = new Date(this.calendarView);
      date.setMonth(date.getMonth() - 1);
      const newDate = date.toISOString().substr(0, 10);
      this.calendarView = newDate;
      this.selectedDate = newDate;
    },

    // Events
    openDialog({ date }) {
      this.dialog = true;
      this.isEditing = false;
      this.eventForm = {
        id: null,
        title: "",
        description: "",
        date: date,
        startTime: "",
      };
    },

    editEvent({ event }) {
      this.dialog = true;
      this.isEditing = true;
      this.eventForm = {
        id: event.id,
        title: event.name.replace(/\(.*\)/, "").trim(),
        description: event.description,
        date: event.start,
        startTime: event.startTime || "",
      };
    },

    async saveEvent() {
      try {
        if (this.isEditing) {
          const eventRef = doc(db, "events", this.eventForm.id);
          await updateDoc(eventRef, {
            title: this.eventForm.title,
            description: this.eventForm.description,
            date: this.eventForm.date,
            startTime: this.eventForm.startTime,
          });
        } else {
          await addDoc(collection(db, "events"), {
            title: this.eventForm.title,
            description: this.eventForm.description,
            date: this.eventForm.date,
            startTime: this.eventForm.startTime,
          });
        }
        this.dialog = false;
        this.loadEvents();
      } catch (error) {
        console.error("Error saving event:", error);
      }
    },

    async deleteEvent() {
      try {
        const eventRef = doc(db, "events", this.eventForm.id);
        await deleteDoc(eventRef);
        this.dialog = false;
        this.loadEvents();
      } catch (error) {
        console.error("Error deleting event:", error);
      }
    },

    async loadEvents() {
      try {
        const querySnapshot = await getDocs(collection(db, "events"));
        this.events = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          name: `${doc.data().title} (${doc.data().startTime || ""})`,
          start: doc.data().date,
          description: doc.data().description,
          startTime: doc.data().startTime || "",
        }));
      } catch (error) {
        console.error("Error loading events:", error);
      }
    },

    // Access Requests
    async loadAccessRequests() {
      try {
        const snapshot = await getDocs(collection(db, "accessRequests"));
        this.accessRequests = snapshot.docs.map((doc) => doc.data());
      } catch (error) {
        console.error("Failed to load access requests:", error);
      }
    },
  },

  mounted() {
    this.loadEvents();
    this.loadAccessRequests();
  },
};
</script>

<style scoped>
.v-card {
  border-radius: 16px;
}
.full-width {
  width: 100%;
}
.v-calendar {
  min-height: 450px;
}
.v-bottom-navigation {
  background-color: #ffffff;
  box-shadow: 0 -2px 6px rgba(0, 0, 0, 0.1);
}
.tab-label {
  font-size: 0.7rem;
  display: block;
  text-align: center;
}
@media (max-width: 600px) {
  .v-sheet {
    height: 450px !important;
  }
}
</style>